import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { get } from 'lodash'
import { FormattedMessage } from 'react-intl'
import { compose } from 'recompose'

import { wizard } from 'helpers/decorators'
import { Title, Button, FormDebug } from 'components/misc'
import {
  Select,
  Input,
  InputPositive,
  InputHidden,
  InputDate
} from 'components/fields'
import { apiAwardUnits } from 'helpers/decorators/api'
import { required, email, maxLength, minLength } from 'utils/validation'
import { nextLevel } from 'constants/wizards'
import { isStepActive } from 'helpers/wizardHelper'

const validate = question => {
  if (question.isOptional) {
    return
  }

  const validations = [required]

  question.maxLength && validations.push(maxLength(question.maxLength))
  question.minLength && validations.push(minLength(question.minLength))
  return validations
}

const enhance = compose(
  apiAwardUnits(),
  wizard({
    form: 'nextLevelRegistration',
    multipage: true,
    destroyOnUnmount: false,
    validate: () => {}
  })
)

class Profile extends Component {
  static propTypes = {
    awardUnits: PropTypes.object.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    changeWizardNext: PropTypes.func,
    wizardSteps: PropTypes.array,
    getAwardQuestions: PropTypes.func.isRequired,
    values: PropTypes.object
  }

  handleSubmit = async fields => {
    const { rootPath, steps } = nextLevel
    const step = isStepActive(this.props.wizardSteps, steps.payment.id) ? steps.payment : steps.privacyPolicy

    await this.props.changeWizardNext(steps.profile.id, rootPath + step.path)
    return this.props.onSubmit(fields)
  }

  componentDidMount() {
    this.props.getAwardQuestions(get(this.props.values, 'organizationId'))
  }

  setQuestionId = (question, nameRoot, field) => (
    <div key={question.id}>
      <InputHidden
        name={`${nameRoot}.profileQuestion.id`}
        value={question.id}
      />
      {field}
    </div>
  )

  findSuperiorAnswer = (answers, question) =>
    answers.find(item => {
      const id = get(item, 'profileQuestion.id')

      return (
        id && id === get(question, 'showIfOptionSelected.profileQuestion.id')
      )
    })

  idDependencyValid = question => {
    if (question.showIfOptionSelected) {
      const valuesAnswers = get(
        this.props,
        'values.person.profileQuestionAnswers'
      )

      if (!valuesAnswers || valuesAnswers.length === 0) {
        return false
      }

      const superiorOptionId = get(
        this.findSuperiorAnswer(valuesAnswers, question),
        'profileQuestionOption.id'
      )
      const dependentOptionId = get(question, 'showIfOptionSelected.id')

      return superiorOptionId && superiorOptionId === dependentOptionId
    }
    return true
  }

  renderQuestion = (question, index) => {
    const nameRoot = `profileQuestionAnswers[${index}]`

    if (this.idDependencyValid(question)) {
      switch (question.questionType) {
        case 'DATE':
          return this.renderDate(question, nameRoot)
        case 'EMAIL':
          return this.renderEmail(question, nameRoot)
        case 'INTEGER':
          return this.renderInteger(question, nameRoot)
        case 'OPTIONS':
          return this.renderOptions(question, nameRoot)
        case 'TEXT':
          return this.renderText(question, nameRoot)
        default:
          break
      }
    }

    return null
  }

  renderOptions = (question, nameRoot) => {
    const mappedOptions = question.profileQuestionOptions.map(({
      id,
      translatedText
    }) => ({
      label: translatedText,
      value: id
    }))

    return (
      mappedOptions.length > 0 &&
      this.setQuestionId(
        question,
        nameRoot,
        <div key={question.id}>
          <Select
            name={`${nameRoot}.profileQuestionOption.id`}
            label={question.translatedText}
            options={mappedOptions}
            validate={required(question)}
          />
        </div>
      )
    )
  }

  renderText = (question, nameRoot) =>
    this.setQuestionId(
      question,
      nameRoot,
      <Input
        name={`${nameRoot}.valueAsString`}
        label={question.translatedText}
        validate={validate(question)}
      />
    )

  renderEmail = (question, nameRoot) =>
    this.setQuestionId(
      question,
      nameRoot,
      <Input
        name={`${nameRoot}.valueAsString`}
        label={question.translatedText}
        validate={[...validate(question), email]}
      />
    )

  renderInteger = (question, nameRoot) =>
    this.setQuestionId(
      question,
      nameRoot,
      <InputPositive
        name={`${nameRoot}.valueAsString`}
        label={question.translatedText}
        type="number"
        validate={validate(question)}
      />
    )

  renderDate = (question, nameRoot) =>
    this.setQuestionId(
      question,
      nameRoot,
      <InputDate
        name={`${nameRoot}.valueAsString`}
        label={question.translatedText}
        validate={!question.isOptional && required}
      />
    )

  render() {
    const { handleSubmit, awardUnits } = this.props
    const questions = get(awardUnits, 'questions')

    return (
      <form
        onSubmit={handleSubmit(this.handleSubmit)}
      >
        <Title h1>
          <FormattedMessage
            id="moduleTitle.profile_questions"
            defaultMessage="Profile Questions"
          />
        </Title>

        <div className="row">
          <div className="col-xs-12 col-md-6">
            {questions &&
              questions.map((item, index) => this.renderQuestion(item, index))}
          </div>
        </div>

        <FormDebug {...this.props} />

        <Button>
          <FormattedMessage id="button.continue" defaultMessage="Continue" />
        </Button>
      </form>
    )
  }
}

export default enhance(Profile)
